import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";
import { baseQueryConfig } from "../api";
import { getToken } from "../models/users.models";
import {
  IBooksWithMeta,
  IGeneratedBooks,
  IProfileStory,
  IUpdateBookRequest,
} from "../models/books.models";

const booksConfig = {
  ...baseQueryConfig,
  baseUrl: baseQueryConfig.baseUrl + "/profileStories",
  prepareHeaders: (headers: Headers) => {
    const session = getToken();
    if (session && session.token) {
      headers.set("Authorization", `Bearer ${session.token}`);
    }
    return headers;
  },
};

export const booksApi = createApi({
  reducerPath: "books",
  tagTypes: ["Books"],
  baseQuery: fetchBaseQuery(booksConfig),
  endpoints: (builder) => ({
    getBooks: builder.query<
      IBooksWithMeta,
      {
        page?: number;
        limit?: number;
        title?: string;
        sortByDate?: string;
      }
    >({
      query: ({ page, limit, title, sortByDate }) => ({
        url: "/search/params",
        params: {
          page,
          limit,
          title,
          sortByDate,
        },
      }),
      providesTags: ["Books"],
    }),
    getGeneratedBooks: builder.query<
      IGeneratedBooks,
      { profileUid: string; limit: number; page: number }
    >({
      query: ({ profileUid, limit, page }) => ({
        url: `/profile/${profileUid}`,
        params: {
          limit,
          page,
        },
      }),
      providesTags: ["Books"],
    }),
    getProfileStories: builder.query<IProfileStory[], any>({
      query: () => ({
        url: "/keyword-categories/all",
      }),
      providesTags: ["Books"],
    }),
    updateBook: builder.mutation<void, IUpdateBookRequest>({
      query: ({ uid, ...data }) => ({
        url: `/${uid}`,
        method: "PATCH",
        body: data,
      }),
      invalidatesTags: ["Books"],
    }),
    deleteBook: builder.mutation<boolean, string>({
      query: (uid) => ({
        url: `/${uid}`,
        method: "DELETE",
      }),
      invalidatesTags: ["Books"],
    }),
    getProfileBooks: builder.query<
      IBooksWithMeta,
      {
        page?: number;
        limit?: number;
        profileUid?: string;
      }
    >({
      query: ({ page, limit, profileUid }) => ({
        url: "/search/params",
        params: {
          page,
          limit,
          profileUid,
        },
      }),
      providesTags: ["Books"],
    }),
    createProfileStoryOption: builder.mutation<void, FormData>({
      query: (formData) => ({
        url: `/keyword-option/${formData.get("categoryId")}`,
        method: "POST",
        body: formData,
      }),
      invalidatesTags: ["Books"],
    }),
    deleteProfileStoryOption: builder.mutation<boolean, { optionId: string }>({
      query: ({ optionId }) => ({
        url: `/keyword-option/${optionId}`,
        method: "DELETE",
      }),
      invalidatesTags: ["Books"],
    }),
    updateProfileStoryOption: builder.mutation<
      void,
      { data: FormData; optionId: string }
    >({
      query: ({ optionId, data }) => ({
        url: `/keyword-option/${optionId}`,
        method: "PATCH",
        body: data,
      }),
      invalidatesTags: ["Books"],
    }),
  }),
});

export const {
  useDeleteBookMutation,
  useGetBooksQuery,
  useUpdateBookMutation,
  useGetProfileBooksQuery,
  useGetProfileStoriesQuery,
  useCreateProfileStoryOptionMutation,
  useDeleteProfileStoryOptionMutation,
  useUpdateProfileStoryOptionMutation,
  useGetGeneratedBooksQuery,
} = booksApi;

